import { Uuid } from "@secretaa/http-contract";
import { z } from "zod";
import { Bindings, ContractBounds, Effect, Identity, Key, Label, Ref, sameRef, sameScope, type Diagnostic } from "./primitives.ts";
import type { RequirementVersion } from "./agreement.ts";
import { canonicalJson } from "../blueprint/canonical-json.ts";

const base = { id: Key, title:Label.optional(), description:Label.optional(), dependsOn: z.array(Key).max(128), criterionIds: z.array(Key).min(1).max(64), inputBindings: Bindings,
  outputContractRef: Ref, failurePolicy: z.enum(["STOP", "CONTINUE_INDEPENDENT", "REQUIRE_DECISION"]), joinPolicy: z.enum(["ALL_SELECTED", "ALL_SUCCESS"]), requiredInputIds: z.array(Key).max(128) };
export const PlanNode = z.discriminatedUnion("kind", [
  z.object({ ...base, kind: z.literal("QUERY"), queryRef: Ref, effect: z.literal("READ") }).strict(),
  z.object({ ...base, kind: z.literal("DATA_TASK"), taskRef: Ref, effect: z.literal("DATA_WRITE") }).strict(),
  z.object({ ...base, kind: z.literal("CAPABILITY_CALL"), capabilityRef: Ref, effect: Effect, targetConstraintRef: Ref }).strict(),
  z.object({ ...base, kind: z.literal("TRANSFORM"), bridgeRef: Ref, effect: z.literal("READ") }).strict(),
  z.object({ ...base, kind: z.literal("AI_PROCESS"), processingRef: Ref, effect: z.literal("READ") }).strict(),
  z.object({ ...base, kind: z.literal("CONDITION"), predicateRef: Ref, branches: z.array(z.object({ key: Key, targetNodeId: Key }).strict()).min(2).max(16), effect: z.literal("READ") }).strict(),
  z.object({ ...base, kind: z.literal("WAIT"), inputRequirementId: Key, deadlinePolicyRef: Ref, effect: z.literal("READ") }).strict(),
  z.object({ ...base, kind: z.literal("APPROVAL"), approvalPolicyRef: Ref, effect: z.literal("READ") }).strict(),
  z.object({ ...base, kind: z.literal("SUBFLOW"), flowRef: Ref, childPolicyRef: Ref, effect: Effect }).strict(),
  z.object({ ...base, kind: z.literal("VERIFY"), verificationPolicyRef: Ref, effect: z.literal("READ") }).strict(),
]);
export const WorkPlanVersion = z.object({ ...Identity, requestId: Uuid, requirementRef: Ref, nodes: z.array(PlanNode).min(1).max(ContractBounds.nodes) }).strict();
export const FlowDefinitionVersion = z.object({ ...Identity, inputContractRef: Ref, outputContractRef: Ref, requirementTemplateRef: Ref, nodes: z.array(PlanNode).min(1).max(ContractBounds.nodes) }).strict();
export type PlanNode = z.infer<typeof PlanNode>;
export type WorkPlanVersion = z.infer<typeof WorkPlanVersion>;
export type FlowDefinitionVersion = z.infer<typeof FlowDefinitionVersion>;

/** SHA-256 của payload này bind confirmation; caller cung cấp crypto ở server hoặc Web Crypto. */
export function canonicalWorkPlan(input: unknown): string {
  return canonicalJson(WorkPlanVersion.parse(input));
}

export function checkWorkPlan(plan: WorkPlanVersion, requirement: RequirementVersion): Diagnostic[] {
  const errors: Diagnostic[] = [];
  const add = (code: string, id = "plan") => errors.push({ code, path: id });
  if (!sameScope(plan.scope, requirement.scope)) add("SCOPE_MISMATCH");
  if (plan.requestId !== requirement.requestId || !sameRef(plan.requirementRef, requirement)) add("REQUIREMENT_MISMATCH");
  errors.push(...checkGraph(plan.nodes, requirement));
  return errors;
}

export function checkFlow(flow: FlowDefinitionVersion, requirement: RequirementVersion): Diagnostic[] {
  const errors = checkGraph(flow.nodes, requirement);
  if (!sameScope(flow.scope, requirement.scope)) errors.push({ code: "SCOPE_MISMATCH", path: "flow" });
  if (!sameRef(flow.requirementTemplateRef, requirement)) errors.push({ code: "REQUIREMENT_MISMATCH", path: "flow" });
  return errors;
}

function checkGraph(graph: PlanNode[], requirement: RequirementVersion): Diagnostic[] {
  const errors: Diagnostic[] = [];
  const add = (code: string, id = "graph") => errors.push({ code, path: id });
  const nodes = new Map(graph.map(n => [n.id, n]));
  if (nodes.size !== graph.length) add("DUPLICATE_NODE");
  const inputs = new Set(requirement.inputs.map(i => i.id));
  const criteria = new Set(requirement.criteria.map(c => c.id));
  const covered = new Set<string>();
  const verified = new Set<string>();
  for (const node of graph) {
    if (new Set(node.dependsOn).size !== node.dependsOn.length) add("DUPLICATE_DEPENDENCY", node.id);
    for (const dep of node.dependsOn) if (!nodes.has(dep) || dep === node.id) add("INVALID_DEPENDENCY", node.id);
    for (const id of node.criterionIds) { if (!criteria.has(id)) add("UNKNOWN_CRITERION", node.id); else covered.add(id); }
    if (node.kind === "VERIFY") for (const id of node.criterionIds) verified.add(id);
    if (node.kind === "CAPABILITY_CALL" && !sameRef(node.targetConstraintRef, requirement.targetConstraintRef)) add("TARGET_CONSTRAINT_MISMATCH", node.id);
    if (new Set(node.criterionIds).size !== node.criterionIds.length || new Set(node.requiredInputIds).size !== node.requiredInputIds.length) add("DUPLICATE_REFERENCE", node.id);
    for (const id of node.requiredInputIds) if (!inputs.has(id)) add("UNKNOWN_INPUT", node.id);
    if (node.kind === "WAIT" && !inputs.has(node.inputRequirementId)) add("UNKNOWN_INPUT", node.id);
    for (const b of node.inputBindings) {
      if (b.source === "INPUT" && !inputs.has(b.key)) add("UNKNOWN_INPUT", node.id);
      if (b.source === "INPUT" && !node.requiredInputIds.includes(b.key)) add("INPUT_GATE_MISSING", node.id);
      if (b.source === "STEP_OUTPUT" && !node.dependsOn.includes(b.key)) add("OUTPUT_NOT_DEPENDENCY", node.id);
    }
    if (node.kind === "CONDITION") {
      if (new Set(node.branches.map(b => b.key)).size !== node.branches.length || new Set(node.branches.map(b => b.targetNodeId)).size !== node.branches.length) add("DUPLICATE_BRANCH", node.id);
      for (const branch of node.branches) if (!nodes.get(branch.targetNodeId)?.dependsOn.includes(node.id)) add("INVALID_BRANCH", node.id);
    }
  }
  for (const id of criteria) if (!covered.has(id)) add("UNCOVERED_CRITERION", id);
  for (const id of criteria) if (!verified.has(id)) add("UNVERIFIED_CRITERION", id);
  for (const input of requirement.inputs) for (const id of input.requiredBeforeNodes) if (!nodes.get(id)?.requiredInputIds.includes(input.id)) add("INPUT_GATE_MISSING", id);
  const visiting = new Set<string>(), done = new Set<string>();
  const visit = (id: string): boolean => {
    if (visiting.has(id)) return false;
    if (done.has(id)) return true;
    visiting.add(id);
    for (const dep of nodes.get(id)?.dependsOn ?? []) if (nodes.has(dep) && !visit(dep)) return false;
    visiting.delete(id); done.add(id); return true;
  };
  for (const id of nodes.keys()) if (!visit(id)) { add("GRAPH_CYCLE"); break; }
  return errors;
}
